import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { InputsConfig } from 'types/form/inputsConfig';
import { useInputsConfig } from 'hooks/form/useInputsConfig';
import { InputRenderer, resolveBase } from './input-factory/types';
import { useInputRegistry, warnMissingRenderer } from './input-factory/registry';

type InputFactoryProps<T extends string> = {
  fieldName: T;
  config: InputsConfig<T>;
  state?: any;
  disabledFields?: T[]
};

/* The single entry point for form fields: resolves the field config, applies
   the cross-cutting concerns (i18n, dynamic disabled, locking through
   disabledFields) then delegates to the renderer registered for the key —
   core + feature renderers assembled at the composition root (src/index.tsx)
   and provided through InputRenderersProvider. */
export const InputFactory = <T extends string>({
  fieldName,
  config,
  state,
  disabledFields,
}: InputFactoryProps<T>) => {
  const { t } = useDynamicTranslation([
    NsKey.Product,
    NsKey.ActorForm,
    NsKey.Mandate,
    NsKey.Borrower,
    NsKey.Pricing,
    NsKey.RealEstate,
    NsKey.Common,
    NsKey.Contractualization
  ]);
  const { renderers } = useInputRegistry();
  const inputsConfig = useInputsConfig<T>(config, disabledFields);

  const fieldConfig = inputsConfig[fieldName];
  if (!fieldConfig) return null;

  const render = renderers[fieldConfig.componentKey] as
    | InputRenderer<typeof fieldConfig.componentKey>
    | undefined;
  if (!render) {
    warnMissingRenderer(fieldConfig.componentKey, fieldName);
    return null;
  }

  const ctx = { t, state };
  return <>{render(fieldConfig, resolveBase(fieldConfig, t, state), ctx)}</>;
};
